import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CANDIDATE_STAGE_LABELS,
  CANDIDATE_STAGE_ORDER,
  TERMINAL_STAGES,
  BG_CHECK_STATUS_LABELS,
  TOKEN_STORAGE_KEYS,
} from '@onboarding-dashboard/shared';
import { formatDateTime } from '@onboarding-dashboard/shared';
import type {
  CandidateDetail,
  CandidateStage,
  DriveAgreement,
  DriveFile,
} from '@onboarding-dashboard/shared';
import { candidateService } from '../../services/candidate.js';
import { useAuth } from '../../contexts/AuthContext.js';
import detailTemplate from './candidate-detail.html?raw';
import './candidate-detail.css';

export function CandidateDetailPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isAdminOrHr = hasRole('admin') || hasRole('hr');

  const [candidate, setCandidate] = useState<CandidateDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history');
  const [driveAgreements, setDriveAgreements] = useState<DriveAgreement[]>([]);
  const [resumeFiles, setResumeFiles] = useState<DriveFile[]>([]);
  const [onboardingDocs, setOnboardingDocs] = useState<DriveFile[]>([]);
  // const [checkrPackages, setCheckrPackages] = useState<CheckrPackage[]>([]);
  // const [checkrPackagesError, setCheckrPackagesError] = useState(false);

  // ─── Helpers ───────────────────────────────────────────

  const showMessage = useCallback((type: 'error' | 'success', message: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLDivElement>(`#detail-${type}`);
    const msgEl = container.querySelector<HTMLSpanElement>(`#detail-${type}-message`);
    if (el && msgEl) {
      msgEl.textContent = message;
      el.style.display = 'block';
      if (type === 'success')
        setTimeout(() => {
          el.style.display = 'none';
        }, 4000);
    }
    const other = type === 'error' ? 'success' : 'error';
    const otherEl = container.querySelector<HTMLDivElement>(`#detail-${other}`);
    if (otherEl) otherEl.style.display = 'none';
  }, []);

  // ─── Data Loading ──────────────────────────────────────

  const loadResumeFiles = useCallback(async () => {
    if (!id) return;
    const result = await candidateService.getResumeFiles(id);
    if (result.success && result.data) {
      setResumeFiles(result.data);
    } else {
      setResumeFiles([]);
    }
  }, [id]);

  const loadOnboardingDocs = useCallback(async () => {
    if (!id) return;
    const result = await candidateService.getOnboardingDocs(id);
    if (result.success && result.data) {
      setOnboardingDocs(result.data);
    } else {
      setOnboardingDocs([]);
    }
  }, [id]);

  const loadDriveAgreements = useCallback(async () => {
    if (!id) return;
    const result = await candidateService.getDriveAgreements(id);
    if (result.success && result.data) {
      setDriveAgreements(result.data);
    } else {
      setDriveAgreements([]);
    }
  }, [id]);

  // const loadCheckrPackages = useCallback(async () => {
  //   const result = await candidateService.listCheckrPackages();
  //   if (result.success && result.data) {
  //     setCheckrPackages(result.data);
  //     setCheckrPackagesError(false);
  //   } else {
  //     setCheckrPackagesError(true);
  //   }
  // }, []);

  const loadCandidate = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    const result = await candidateService.getById(id);
    if (result.success && result.data) {
      setCandidate(result.data);
    } else {
      showMessage('error', result.error?.message ?? 'Failed to load candidate');
    }
    setIsLoading(false);
  }, [id, showMessage]);

  useEffect(() => {
    void loadCandidate();
    void loadResumeFiles();
    void loadOnboardingDocs();
    if (isAdminOrHr) {
      void loadDriveAgreements();
    }
  }, [loadCandidate, loadDriveAgreements, loadResumeFiles, loadOnboardingDocs, isAdminOrHr]);

  // ─── Render Profile ────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const loading = container.querySelector<HTMLDivElement>('#detail-loading');
    const content = container.querySelector<HTMLDivElement>('#detail-content');

    if (isLoading) {
      if (loading) loading.style.display = '';
      if (content) content.style.display = 'none';
      return;
    }
    if (loading) loading.style.display = 'none';
    if (!candidate) return;
    if (content) content.style.display = '';

    // Profile header
    const nameEl = container.querySelector<HTMLHeadingElement>('#candidate-name');
    const roleEl = container.querySelector<HTMLParagraphElement>('#candidate-role-title');
    const emailEl = container.querySelector<HTMLSpanElement>('#candidate-email-display');
    const phoneEl = container.querySelector<HTMLSpanElement>('#candidate-phone-display');
    const stageBadge = container.querySelector<HTMLSpanElement>('#candidate-stage-badge');

    if (nameEl) nameEl.textContent = `${candidate.firstName} ${candidate.lastName}`;
    if (roleEl)
      roleEl.textContent = `${candidate.openRole.title} — ${candidate.openRole.department}`;
    if (emailEl) emailEl.textContent = candidate.email;
    if (phoneEl) phoneEl.textContent = candidate.phone ?? '';
    if (stageBadge) {
      stageBadge.textContent =
        CANDIDATE_STAGE_LABELS[candidate.currentStage] ?? candidate.currentStage;
      stageBadge.className = `stage-badge stage-badge--${candidate.currentStage}`;
    }

    // Stage transition buttons
    const stageButtons = container.querySelector<HTMLDivElement>('#stage-buttons');
    const stageActions = container.querySelector<HTMLDivElement>('#stage-actions');
    if (stageButtons && stageActions) {
      if ((TERMINAL_STAGES as string[]).includes(candidate.currentStage)) {
        stageActions.style.display = 'none';
      } else {
        stageActions.style.display = '';
        const currentIdx = CANDIDATE_STAGE_ORDER.indexOf(candidate.currentStage);
        const nextStage =
          currentIdx >= 0 && currentIdx < CANDIDATE_STAGE_ORDER.length - 1
            ? CANDIDATE_STAGE_ORDER[currentIdx + 1]
            : null;

        let buttonsHtml = '';
        if (nextStage) {
          buttonsHtml += `<button class="stage-btn" data-to-stage="${nextStage}">→ ${escapeHtml(CANDIDATE_STAGE_LABELS[nextStage] ?? nextStage)}</button>`;
        }
        buttonsHtml += `<button class="stage-btn stage-btn--danger" data-to-stage="rejected">Reject</button>`;
        buttonsHtml += `<button class="stage-btn stage-btn--danger" data-to-stage="withdrawn">Withdrawn</button>`;
        stageButtons.innerHTML = buttonsHtml;
      }
    }

    // History timeline
    renderHistory(container, candidate);

    // Drive folder link in profile header (admin/hr only)
    const driveLink = container.querySelector<HTMLAnchorElement>('#candidate-drive-link');
    if (driveLink) {
      if (isAdminOrHr && candidate.driveFolderUrl) {
        driveLink.href = candidate.driveFolderUrl;
        driveLink.style.display = '';
      } else {
        driveLink.style.display = 'none';
      }
    }

    // Hide restricted tabs for non-admin/hr users
    const agreementsTab = container.querySelector<HTMLButtonElement>('[data-tab="agreements"]');
    const bgCheckTab = container.querySelector<HTMLButtonElement>('[data-tab="bg-check"]');
    if (agreementsTab) agreementsTab.style.display = isAdminOrHr ? '' : 'none';
    if (bgCheckTab) bgCheckTab.style.display = isAdminOrHr ? '' : 'none';

    // Interviews
    renderInterviews(container, candidate);

    // Agreements (Drive-based)
    renderDriveAgreements(container, driveAgreements);

    // Background check
    renderBackgroundCheck(container, candidate);

    // Onboarding Documents (Drive-based)
    renderOnboardingDocs(container, onboardingDocs);

    // Resume
    renderResume(container, resumeFiles, id ?? '');
  }, [candidate, isLoading, isAdminOrHr, driveAgreements, resumeFiles, onboardingDocs]);

  // ─── Tab switching ─────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tabs = container.querySelectorAll<HTMLButtonElement>('.tab-btn');
    const panels = ['history', 'resume', 'interviews', 'agreements', 'bg-check', 'onboarding-docs'];

    tabs.forEach((btn) => {
      if (btn.dataset.tab === activeTab) {
        btn.classList.add('tab-btn--active');
      } else {
        btn.classList.remove('tab-btn--active');
      }
    });

    panels.forEach((p) => {
      const panel = container.querySelector<HTMLDivElement>(`#panel-${p}`);
      if (panel) {
        if ((p === 'agreements' || p === 'bg-check') && !isAdminOrHr) {
          panel.style.display = 'none';
        } else {
          panel.style.display = p === activeTab ? '' : 'none';
        }
      }
    });
  }, [activeTab, isAdminOrHr]);

  // Reset to safe tab if user is on a restricted tab
  useEffect(() => {
    if (!isAdminOrHr && (activeTab === 'agreements' || activeTab === 'bg-check')) {
      setActiveTab('history');
    }
  }, [isAdminOrHr, activeTab]);

  // ─── Event Listeners ──────────────────────────────────

  const handleStageTransition = useCallback(
    async (toStage: string, btn?: HTMLButtonElement) => {
      if (!id) return;

      const container = containerRef.current;
      const allStageBtns = container?.querySelectorAll<HTMLButtonElement>('.stage-btn');

      // Disable all stage buttons while transitioning
      allStageBtns?.forEach((b) => {
        b.disabled = true;
      });
      if (btn && toStage === 'onboarding_docs') {
        btn.textContent = 'Copying documents…';
      }

      const result = await candidateService.transitionStage(id, {
        toStage: toStage as CandidateStage,
      });
      if (result.success) {
        showMessage('success', `Moved to ${CANDIDATE_STAGE_LABELS[toStage] ?? toStage}`);
        void loadCandidate();
      } else {
        allStageBtns?.forEach((b) => {
          b.disabled = false;
        });
        if (btn && toStage === 'onboarding_docs') {
          btn.textContent = `→ ${CANDIDATE_STAGE_LABELS['onboarding_docs'] ?? 'onboarding_docs'}`;
        }
        showMessage('error', result.error?.message ?? 'Failed to transition stage');
      }
    },
    [id, showMessage, loadCandidate]
  );

  // ─── Resume Handlers ──────────────────────────────

  const handleUploadResume = useCallback(
    async (file: File) => {
      if (!id) return;
      const result = await candidateService.uploadResume(id, file);
      if (result.success) {
        showMessage('success', 'Resume uploaded');
        void loadResumeFiles();
      } else {
        showMessage('error', result.error?.message ?? 'Failed to upload resume');
      }
    },
    [id, showMessage, loadResumeFiles]
  );

  // ─── Agreement Handlers ─────────────────────────────

  const handleToggleSigned = useCallback(
    async (driveFileId: string, signed: boolean) => {
      if (!id) return;
      const result = await candidateService.toggleAgreementSigned(id, driveFileId, signed);
      if (result.success) {
        showMessage('success', signed ? 'Marked as signed' : 'Unmarked as signed');
        void loadDriveAgreements();
      } else {
        showMessage('error', result.error?.message ?? 'Failed to update agreement');
      }
    },
    [id, showMessage, loadDriveAgreements]
  );

  const handleUploadAgreement = useCallback(
    async (file: File) => {
      if (!id) return;
      const result = await candidateService.uploadAgreement(id, file);
      if (result.success) {
        showMessage('success', 'Agreement uploaded');
        void loadDriveAgreements();
      } else {
        showMessage('error', result.error?.message ?? 'Failed to upload agreement');
      }
    },
    [id, showMessage, loadDriveAgreements]
  );

  // ─── Background Check Handlers ──────────────────────

  const handleInitiateBgCheck = useCallback(async () => {
    if (!id) return;
    const result = await candidateService.initiateBackgroundCheck({ candidateId: id });
    if (result.success) {
      showMessage('success', 'Background check initiated');
      void loadCandidate();
    } else {
      showMessage('error', result.error?.message ?? 'Failed to initiate background check');
    }
  }, [id, showMessage, loadCandidate]);

  const handleUpdateBgCheck = useCallback(
    async (checkId: string, status: string) => {
      const result = await candidateService.updateBackgroundCheck(checkId, {
        status: status as
          | 'pending'
          | 'initiated'
          | 'in_progress'
          | 'passed'
          | 'failed'
          | 'cancelled',
      });
      if (result.success) {
        showMessage(
          'success',
          `Background check marked as ${BG_CHECK_STATUS_LABELS[status] ?? status}`
        );
        void loadCandidate();
      } else {
        showMessage('error', result.error?.message ?? 'Failed to update background check');
      }
    },
    [showMessage, loadCandidate]
  );

  // ─── Event Listeners ──────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Back link
    const backLink = container.querySelector<HTMLAnchorElement>('#back-link');
    const handleBack = (e: Event): void => {
      e.preventDefault();
      navigate('/pipeline');
    };
    backLink?.addEventListener('click', handleBack);

    // Tab buttons
    const handleTabClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.tab-btn');
      if (!btn?.dataset.tab) return;
      if ((btn.dataset.tab === 'agreements' || btn.dataset.tab === 'bg-check') && !isAdminOrHr)
        return;
      setActiveTab(btn.dataset.tab);
    };
    const tabContainer = container.querySelector<HTMLDivElement>('.detail-tabs');
    tabContainer?.addEventListener('click', handleTabClick);

    // Stage transition buttons
    const handleStageClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.stage-btn');
      if (btn?.dataset.toStage) {
        void handleStageTransition(btn.dataset.toStage, btn);
      }
    };
    const stageButtons = container.querySelector<HTMLDivElement>('#stage-buttons');
    stageButtons?.addEventListener('click', handleStageClick);

    // Agreement toggle signed buttons (event delegation)
    const agreementsContent = container.querySelector<HTMLDivElement>('#agreements-content');
    const handleAgreementToggle = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-toggle-signed]');
      if (!btn) return;
      const driveFileId = btn.dataset.toggleSigned;
      const signed = btn.dataset.currentSigned === 'false';
      if (driveFileId) {
        void handleToggleSigned(driveFileId, signed);
      }
    };
    agreementsContent?.addEventListener('click', handleAgreementToggle);

    // Agreement file upload (event delegation)
    const handleAgreementUpload = (e: Event): void => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'agreement-file-input') return;
      const file = input.files?.[0];
      if (file) {
        void handleUploadAgreement(file);
        input.value = '';
      }
    };
    agreementsContent?.addEventListener('change', handleAgreementUpload);

    // Resume file upload (event delegation on panel)
    const resumeContent = container.querySelector<HTMLDivElement>('#resume-content');
    const handleResumeChange = (e: Event): void => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'resume-file-input') return;
      const file = input.files?.[0];
      if (file) {
        void handleUploadResume(file);
      }
    };
    resumeContent?.addEventListener('change', handleResumeChange);

    // Resume download via backend proxy (event delegation)
    const handleResumeDownload = (e: Event): void => {
      const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('[data-resume-download]');
      if (!link || link.classList.contains('loading')) return;
      e.preventDefault();
      const url = link.dataset.resumeDownload!;
      const originalText = link.textContent ?? '';
      link.textContent = 'Opening…';
      link.classList.add('loading');
      const token = localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
      void fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => {
          if (!res.ok) throw new Error('Download failed');
          return res.blob();
        })
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        })
        .catch(() => {
          showMessage('error', 'Failed to download resume');
        })
        .finally(() => {
          link.textContent = originalText;
          link.classList.remove('loading');
        });
    };
    resumeContent?.addEventListener('click', handleResumeDownload);

    // Background check buttons (event delegation)
    const bgCheckPanel = container.querySelector<HTMLDivElement>('#panel-bg-check');
    const handleBgCheckAction = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-bg-action]');
      if (!btn) return;
      const action = btn.dataset.bgAction;
      if (action === 'initiate') {
        void handleInitiateBgCheck();
      } else if (action && btn.dataset.bgId) {
        void handleUpdateBgCheck(btn.dataset.bgId, action);
      }
    };
    bgCheckPanel?.addEventListener('click', handleBgCheckAction);

    return () => {
      backLink?.removeEventListener('click', handleBack);
      tabContainer?.removeEventListener('click', handleTabClick);
      stageButtons?.removeEventListener('click', handleStageClick);
      agreementsContent?.removeEventListener('click', handleAgreementToggle);
      agreementsContent?.removeEventListener('change', handleAgreementUpload);
      resumeContent?.removeEventListener('change', handleResumeChange);
      resumeContent?.removeEventListener('click', handleResumeDownload);
      bgCheckPanel?.removeEventListener('click', handleBgCheckAction);
    };
  }, [
    navigate,
    isAdminOrHr,
    handleStageTransition,
    handleToggleSigned,
    handleUploadAgreement,
    handleUploadResume,
    handleInitiateBgCheck,
    handleUpdateBgCheck,
    showMessage,
  ]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: detailTemplate }} />;
}

// ─── Render Helpers ──────────────────────────────────

function renderHistory(container: HTMLElement, candidate: CandidateDetail): void {
  const timeline = container.querySelector<HTMLDivElement>('#history-timeline');
  if (!timeline) return;

  if (candidate.stageHistory.length === 0) {
    timeline.innerHTML = '<div class="empty-state">No stage history yet.</div>';
    return;
  }

  timeline.innerHTML = candidate.stageHistory
    .map((h) => {
      const from = h.fromStage ? (CANDIDATE_STAGE_LABELS[h.fromStage] ?? h.fromStage) : 'New';
      const to = CANDIDATE_STAGE_LABELS[h.toStage] ?? h.toStage;
      const changedBy = h.changedBy ? `${h.changedBy.firstName} ${h.changedBy.lastName}` : 'System';
      return `
        <div class="timeline-item">
          <div class="timeline-dot"></div>
          <div class="timeline-content">
            <div class="timeline-title">${escapeHtml(from)} → ${escapeHtml(to)}</div>
            <div class="timeline-meta">by ${escapeHtml(changedBy)} · ${formatDateTime(h.createdAt)}</div>
            ${h.note ? `<div class="timeline-note">${escapeHtml(h.note)}</div>` : ''}
          </div>
        </div>
      `;
    })
    .join('');
}

function renderInterviews(container: HTMLElement, candidate: CandidateDetail): void {
  const el = container.querySelector<HTMLDivElement>('#interviews-content');
  if (!el) return;

  if (candidate.interviews.length === 0) {
    el.innerHTML = '<div class="empty-state">No interviews scheduled yet.</div>';
    return;
  }

  el.innerHTML = candidate.interviews
    .map((i) => {
      const interviewer = i.interviewer
        ? `${i.interviewer.firstName} ${i.interviewer.lastName}`
        : '—';

      // When scheduledAt is null, the link was sent but no time booked yet
      const linkSent = i.status === 'scheduled' && !i.scheduledAt;
      const statusBadgeClass = linkSent ? 'link-sent' : i.status;
      const statusLabel = linkSent ? 'Link sent' : i.status.replace('_', ' ');

      const ratingStars = i.rating ? '★'.repeat(i.rating) + '☆'.repeat(5 - i.rating) : '';

      // Build meta line: scheduled date (if set), rating
      const metaParts: string[] = [];
      if (i.scheduledAt) metaParts.push(formatDateTime(i.scheduledAt));
      if (ratingStars) metaParts.push(`<span class="interview-rating">${ratingStars}</span>`);
      const metaHtml = metaParts.length > 0 ? metaParts.join(' · ') : '';

      return `
        <div class="detail-card">
          <div class="detail-card-title">${escapeHtml(interviewer)}</div>
          ${metaHtml ? `<div class="detail-card-meta">${metaHtml}</div>` : ''}
          <span class="interview-status-badge interview-status-badge--${statusBadgeClass}">${escapeHtml(statusLabel)}</span>
          ${i.feedback ? `<div class="timeline-note" style="margin-top: 0.5rem;">${escapeHtml(i.feedback)}</div>` : ''}
        </div>
      `;
    })
    .join('');
}

function renderDriveAgreements(container: HTMLElement, agreements: DriveAgreement[]): void {
  const el = container.querySelector<HTMLDivElement>('#agreements-content');
  if (!el) return;

  const uploadHtml = `
    <div class="agreement-upload-area">
      <label class="btn btn--accent btn--small">
        Upload Agreement
        <input type="file" id="agreement-file-input" style="display: none;" accept=".pdf,.doc,.docx" />
      </label>
    </div>
  `;

  if (agreements.length === 0) {
    el.innerHTML = `${uploadHtml}<div class="empty-state">No agreements in Drive folder yet.</div>`;
    return;
  }

  const STATUS_LABELS: Record<string, string> = {
    draft: 'Draft',
    sent: 'Sent',
    signed: 'Signed',
  };

  const rowsHtml = agreements
    .map((a) => {
      const statusLabel = STATUS_LABELS[a.status] ?? a.status;

      const pdfLink = a.pdfUrl
        ? `<a class="agreement-pdf-link" href="${escapeHtml(a.pdfUrl)}" target="_blank" rel="noopener">View PDF</a>`
        : '';

      let toggleBtn = '';
      if (a.status === 'sent' || a.status === 'signed') {
        const label = a.markedSigned ? 'Unmark Signed' : 'Mark Signed';
        toggleBtn = `<button class="toggle-signed-btn" data-toggle-signed="${escapeHtml(a.id)}" data-current-signed="${String(a.markedSigned)}">${label}</button>`;
      }

      return `
        <div class="agreement-file-row">
          <a class="agreement-file-name" href="${escapeHtml(a.url)}" target="_blank" rel="noopener">${escapeHtml(a.name)}</a>
          <span class="agreement-status-badge agreement-status-badge--${a.status}">${escapeHtml(statusLabel)}</span>
          ${pdfLink}
          ${toggleBtn}
        </div>
      `;
    })
    .join('');

  el.innerHTML = `${uploadHtml}${rowsHtml}`;
}

function renderBackgroundCheck(container: HTMLElement, candidate: CandidateDetail): void {
  const actionsEl = container.querySelector<HTMLDivElement>('#bg-check-actions');
  const contentEl = container.querySelector<HTMLDivElement>('#bg-check-content');
  if (!actionsEl || !contentEl) return;

  const isTerminal = (TERMINAL_STAGES as string[]).includes(candidate.currentStage);

  if (!candidate.backgroundCheck) {
    if (isTerminal) {
      actionsEl.innerHTML = '';
      contentEl.innerHTML = '<div class="empty-state">No background check initiated.</div>';
      return;
    }

    actionsEl.innerHTML = '';
    contentEl.innerHTML = `
      <div class="empty-state">
        <p>No background check initiated yet.</p>
        <button class="btn btn--accent" data-bg-action="initiate" style="margin-top: var(--sp-3);">Mark as Sent</button>
      </div>
    `;
    return;
  }

  const bg = candidate.backgroundCheck;
  const statusLabel = BG_CHECK_STATUS_LABELS[bg.status] ?? bg.status;

  let actionsHtml = '';
  if (
    !isTerminal &&
    bg.status !== 'passed' &&
    bg.status !== 'failed' &&
    bg.status !== 'cancelled'
  ) {
    actionsHtml += `<button class="btn btn--accent btn--small" data-bg-action="passed" data-bg-id="${bg.id}">Mark as Completed</button>`;
    actionsHtml += ` <button class="btn btn--danger btn--small" data-bg-action="cancelled" data-bg-id="${bg.id}">Cancel</button>`;
  }

  actionsEl.innerHTML = '';

  contentEl.innerHTML = `
    <div class="detail-card">
      <div class="detail-card-title">
        Background Check
        <span class="bg-check-status-badge bg-check-status-badge--${bg.status}">${escapeHtml(statusLabel)}</span>
      </div>
      <div class="detail-card-meta">
        ${bg.initiatedAt ? `Initiated: ${formatDateTime(bg.initiatedAt)}` : ''}
        ${bg.completedAt ? ` · Completed: ${formatDateTime(bg.completedAt)}` : ''}
      </div>
      ${actionsHtml ? `<div class="detail-card-actions">${actionsHtml}</div>` : ''}
    </div>
  `;
}

function renderOnboardingDocs(container: HTMLElement, files: DriveFile[]): void {
  const el = container.querySelector<HTMLDivElement>('#onboarding-docs-content');
  if (!el) return;

  if (files.length === 0) {
    el.innerHTML = '<div class="empty-state">No onboarding documents in Drive folder yet.</div>';
    return;
  }

  el.innerHTML = files
    .map((f) => {
      const ext = f.mimeType.split('/').pop() ?? 'file';
      return `
        <div class="agreement-file-row">
          <a class="agreement-file-name" href="${escapeHtml(f.url)}" target="_blank" rel="noopener">${escapeHtml(f.name)}</a>
          <span class="doc-type-badge">${escapeHtml(ext)}</span>
        </div>
      `;
    })
    .join('');
}

function renderResume(container: HTMLElement, files: DriveFile[], candidateId: string): void {
  const el = container.querySelector<HTMLDivElement>('#resume-content');
  if (!el) return;

  if (files.length === 0) {
    el.innerHTML = `
      <div class="resume-upload-area">
        <p>No resume uploaded yet.</p>
        <label class="btn btn--accent btn--small">
          Choose File
          <input type="file" id="resume-file-input" style="display: none;" accept=".pdf,.doc,.docx" />
        </label>
      </div>
    `;
    return;
  }

  const file = files[0]!;
  const downloadUrl = candidateService.getResumeDownloadUrl(candidateId, file.id);
  el.innerHTML = `
    <div class="resume-file-display">
      <a class="agreement-file-name" href="#" data-resume-download="${escapeHtml(downloadUrl)}" data-resume-name="${escapeHtml(file.name)}">${escapeHtml(file.name)}</a>
      <span class="doc-type-badge">${escapeHtml(file.mimeType.split('/').pop() ?? 'file')}</span>
    </div>
  `;
}

// ─── Utility ──────────────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
